import { createTheme } from "@mui/material";
import { merge } from "lodash";
import { resolveThemeColors } from "./tenantBranding";

/** Global Matx activeTheme key for hospital branding. */
export const TENANT_THEME_KEY = "tenantBrand";
/** Topbar/footer Matx theme key — keeps light chrome, hospital primary as accent. */
export const TENANT_TOPBAR_THEME_KEY = "tenantBrandTopbar";

const PLATFORM_ACTIVE_THEME = "blue";
const PLATFORM_TOPBAR_THEME = "whiteBlue";

/**
 * Apply hospital theme colors onto Matx settings without destroying presets.
 * Single runtime path: hospital branding → Matx themes map → ThemeProvider.
 * Falls back to the platform "blue" / "whiteBlue" Matx defaults when colors are unset.
 */
export function applyTenantThemeToSettings(settings, updateSettings, themeInput) {
  if (!settings?.themes || typeof updateSettings !== "function") {
    return;
  }

  const baseKey =
    settings.activeTheme &&
    settings.activeTheme !== TENANT_THEME_KEY &&
    settings.activeTheme !== TENANT_TOPBAR_THEME_KEY
      ? settings.activeTheme
      : PLATFORM_ACTIVE_THEME;
  const baseTheme = settings.themes[baseKey] || settings.themes[PLATFORM_ACTIVE_THEME];
  if (!baseTheme) {
    return;
  }

  const { primaryColor, secondaryColor } = resolveThemeColors(themeInput);
  const hasCustom =
    themeInput && (themeInput.primaryColor || themeInput.secondaryColor);

  if (!hasCustom) {
    const patch = {};
    if (
      settings.activeTheme === TENANT_THEME_KEY ||
      settings.activeTheme === TENANT_TOPBAR_THEME_KEY
    ) {
      patch.activeTheme =
        baseKey === TENANT_THEME_KEY || baseKey === TENANT_TOPBAR_THEME_KEY
          ? PLATFORM_ACTIVE_THEME
          : baseKey;
    }
    if (settings.layout1Settings?.topbar?.theme === TENANT_TOPBAR_THEME_KEY) {
      patch.layout1Settings = { topbar: { theme: PLATFORM_TOPBAR_THEME } };
    }
    if (settings.footer?.theme === TENANT_TOPBAR_THEME_KEY) {
      patch.footer = { theme: PLATFORM_TOPBAR_THEME };
    }
    if (Object.keys(patch).length) {
      updateSettings(patch);
    }
    return;
  }

  // Idempotent: avoid re-creating themes when colors are already applied.
  // Prevents settings churn that can starve Suspense route commits.
  const existing = settings.themes?.[TENANT_THEME_KEY];
  const alreadyApplied =
    settings.activeTheme === TENANT_THEME_KEY &&
    existing?.palette?.primary?.main?.toUpperCase?.() === primaryColor.toUpperCase() &&
    existing?.palette?.secondary?.main?.toUpperCase?.() === secondaryColor.toUpperCase() &&
    settings.layout1Settings?.topbar?.theme === TENANT_TOPBAR_THEME_KEY &&
    settings.footer?.theme === TENANT_TOPBAR_THEME_KEY;
  if (alreadyApplied) {
    return;
  }

  const tenantTheme = createTheme(
    merge({}, baseTheme, {
      palette: {
        primary: { main: primaryColor },
        secondary: { main: secondaryColor },
      },
    }),
  );

  // Keep Matx light topbar chrome (whiteBlue structure); use hospital primary as accent.
  const topbarBase =
    settings.themes[PLATFORM_TOPBAR_THEME] || settings.themes.whiteBlue || baseTheme;
  const topbarTheme = createTheme(
    merge({}, topbarBase, {
      palette: {
        secondary: { main: primaryColor, contrastText: "#ffffff" },
      },
    }),
  );

  updateSettings({
    themes: {
      [TENANT_THEME_KEY]: tenantTheme,
      [TENANT_TOPBAR_THEME_KEY]: topbarTheme,
    },
    activeTheme: TENANT_THEME_KEY,
    layout1Settings: {
      topbar: { theme: TENANT_TOPBAR_THEME_KEY },
    },
    footer: {
      theme: TENANT_TOPBAR_THEME_KEY,
    },
  });
}
